import {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { Preload } from 'ontraccr-common';

import Debouncer from '../../helpers/Debouncer';
import { isNullOrUndefined } from '../../helpers/helpers';
import { getLinkId, loadForms } from '../../forms/formHelpers';

const getStartEndTimes = ({
  preloadDateField,
  preloadDateModifier = 'days',
  preloadDateCount = 1,
  linkedStart,
  linkedEnd,
}) => {
  const now = DateTime.local();
  const startTimeToFilter = preloadDateField
    ? linkedStart
    : now.startOf('day').minus({ [preloadDateModifier]: preloadDateCount }).plus({ days: 1 });
  const endTimeToFilter = preloadDateField ? linkedEnd : now.endOf('day');
  return {
    startTime: startTimeToFilter,
    endTime: endTimeToFilter,
  };
};

const getRelevantForms = async ({
  preloadDateField,
  preloadDateModifier = 'days',
  preloadDateCount,
  preloadProject,
  preloadForm,
  formSubmissionIds,
  linkedStart,
  linkedEnd,
  linkedProjectId,
  linkedFormTemplateIds,
  filterByTimeTrackingGridDate = false,
}) => {
  try {
    const { startTime, endTime } = getStartEndTimes({
      preloadDateField,
      preloadDateModifier,
      preloadDateCount,
      linkedStart,
      linkedEnd,
    });
    const forms = await loadForms({
      projectId: preloadProject || linkedProjectId,
      formTemplateId: preloadForm,
      formTemplateIds: linkedFormTemplateIds,
      formSubmissionIds,
      startTime: startTime.toMillis(),
      endTime: endTime.toMillis(),
      filterByTimeTrackingGridDate,
    });

    return forms;
  } catch (err) {
    Sentry.captureException(err);
    return null;
  }
};

const getRelevantTimecardTables = async ({
  timecardPreloadTables,
  divisions,
  preloadDateField,
  preloadDateModifier,
  preloadDateCount,
  preloadProject,
  linkedStart,
  linkedEnd,
  linkedProjectId,
}) => {
  try {
    const {
      startTime,
      endTime,
    } = getStartEndTimes({
      preloadDateField,
      preloadDateModifier,
      preloadDateCount,
      linkedStart,
      linkedEnd,
    });
    const { data } = await axios.get('/tasks/tables', {
      params: {
        tableIds: timecardPreloadTables,
        divisionIds: divisions,
        projectId: preloadProject ?? linkedProjectId,
        startDate: startTime.toISODate(),
        endDate: endTime.toISODate(),
      },
    });

    return data;
  } catch (err) {
    Sentry.captureException(err);
    return null;
  }
};

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

const filterTableItemsByCostcode = ({
  tableItems,
  costcodeName,
  costcodeCode,
  costcodeMap,
}) => {
  if (!costcodeName || !costcodeCode) return tableItems;

  return tableItems.filter((item) => {
    const { costcodeId: rowCostcodeId } = item;

    if (!rowCostcodeId) return false;
    const valueCostcodeObj = costcodeMap[rowCostcodeId];
    if (!valueCostcodeObj) return false;

    return valueCostcodeObj.name === costcodeName
      && valueCostcodeObj.code === costcodeCode;
  });
};

const filterTimeTrackingGridValuesByCostcode = ({
  timeTrackingGridValues,
  columns = [],
  costcodeName,
  costcodeCode,
}) => {
  const hasCostcodeFilter = !isNullOrUndefined(costcodeName) && !isNullOrUndefined(costcodeCode);

  const columnsToUse = hasCostcodeFilter
    ? columns.filter((col) => {
      const cc = col.costcode;
      if (!cc) return false;
      return cc.name === costcodeName && cc.code === costcodeCode;
    })
    : columns;

  if (columnsToUse.length === 0) return [];

  return columnsToUse.flatMap((column) => timeTrackingGridValues.map((value) => {
    const rowData = value[column.rowId];
    return {
      ...value,
      ...(rowData && typeof rowData === 'object' ? rowData : {}),
    };
  }));
};

export default function usePreloadHook({
  responses,
  id,
  setResponses,
  columns,
  responding,
  configProps,
  type,
  setLoading,
  divisions,
  sectionParser,
  isTimeTrackingGrid = false,
  costcodeMap = {},
  skipPreload = false,
}) {
  const {
    preloadExistingEntries, // Whether to preload existing entries at all
    preloadProject, // Specific Project ID
    preloadForm, // Specific Form Template ID
    preloadDropdown, // Project ID from a dropdown
    preloadFormDropdown, // Form Template ID from a dropdown
    preloadDateField, // Date field to preload
    preloadDateModifier, // Hard coded date modifier to use (e.g. days, weeks)
    preloadDateCount, // Hard coded Date count to use (e.g. 1, 2, 3).
    preloadSubmissionDropdown, // Form Submission IDs from a dropdown
    timecardPreloadTables, // Timecard Tables to preload
    preloadCostcodeDropdown, // Costcode ID from a dropdown
    preloadCostcodeName, // Costcode name (stored when preloadCostcode is set)
    preloadCostcodeCode, // Costcode code (stored when preloadCostcode is set)
  } = configProps ?? {};

  const [lastGetConfig, setLastGetConfig] = useState(null);
  const [debouncer] = useState(new Debouncer());

  const linkedProjectId = useMemo(() => (
    getLinkId({ responses, defaultValue: null, linkField: preloadDropdown }) ?? null
  ), [responses, preloadDropdown]);

  const linkedFormTemplateIds = useMemo(() => (
    getLinkId({
      responses,
      defaultValue: null,
      linkField: preloadFormDropdown,
      allowMultiple: isTimeTrackingGrid,
    }) ?? null
  ), [responses, preloadFormDropdown, isTimeTrackingGrid]);

  const linkedDateValue = useMemo(() => {
    if (!preloadDateField) return null;
    const {
      [preloadDateField]: dateResponse = {},
    } = responses;

    const { startTime, endTime, date } = dateResponse;

    if (date) { // date-time
      return {
        startTime: DateTime.fromMillis(date).startOf('day').toMillis(),
        endTime: DateTime.fromMillis(date).endOf('day').toMillis(),
      };
    }

    if (startTime && endTime) { // date-time range
      return {
        startTime: DateTime.fromMillis(startTime).startOf('day').toMillis(),
        endTime: DateTime.fromMillis(endTime).endOf('day').toMillis(),
      };
    }

    return null;
  }, [responses, preloadDateField]);

  const linkedSubmissionDropdown = useMemo(() => (
    responses?.[preloadSubmissionDropdown]
  ), [responses, preloadSubmissionDropdown]);

  const linkedCostcodeId = useMemo(() => {
    if (!preloadCostcodeDropdown) return null;
    const ourCostcodeId = getLinkId({
      responses,
      defaultValue: null,
      linkField: preloadCostcodeDropdown,
    }) ?? null;
    if (!ourCostcodeId) return null;
    const splitCostcodeId = ourCostcodeId?.split('.');
    if (splitCostcodeId?.length === 2) {
      return splitCostcodeId[1];
    }
    return ourCostcodeId;
  }, [responses, preloadCostcodeDropdown]);

  const linkedCostcodeName = useMemo(() => {
    if (preloadCostcodeName) return preloadCostcodeName;
    if (!linkedCostcodeId || !costcodeMap[linkedCostcodeId]) return null;
    return costcodeMap[linkedCostcodeId]?.name ?? null;
  }, [preloadCostcodeName, linkedCostcodeId, costcodeMap]);

  const linkedCostcodeCode = useMemo(() => {
    if (preloadCostcodeCode) return preloadCostcodeCode;
    if (!linkedCostcodeId || !costcodeMap[linkedCostcodeId]) return null;
    return costcodeMap[linkedCostcodeId]?.code ?? null;
  }, [preloadCostcodeCode, linkedCostcodeId, costcodeMap]);

  const preloadAll = useCallback(async (forceReload = false) => {
    if (skipPreload) return;

    const linkedSubmissionIds = linkedSubmissionDropdown?.selected?.map?.((v) => v.id)
      ?? linkedSubmissionDropdown?.values?.map?.((v) => v.id)
      ?? [];

    if (!forceReload && Preload.shouldSkipPreload({
      responding,
      preloadExistingEntries,
      preloadProject,
      lastGetConfig,
      preloadDateField,
      linkedDateValue,
      preloadDropdown,
      linkedProjectId,
      preloadFormDropdown,
      preloadForm,
      linkedFormTemplateIds: normalizeToArray(linkedFormTemplateIds),
      preloadSubmissionDropdown,
      linkedSubmissionIds,
      timecardPreloadTables,
      preloadCostcodeDropdown,
      linkedCostcodeName,
      linkedCostcodeCode,
    })) {
      return;
    }

    const existingValues = responses?.[id]?.values;
    if (existingValues?.length > 0 && !forceReload) {
      const hasNonPreloadedValues = existingValues.some((value) => !value.isPreloaded);
      if (hasNonPreloadedValues) {
        return;
      }
    }

    if (typeof setLoading === 'function') setLoading(true);
    const { startTime: linkedStart, endTime: linkedEnd } = linkedDateValue ?? {};
    let relevantValues = [];
    let relevantColumns = [];
    const hasProject = preloadProject || linkedProjectId;
    const hasPreloadForm = Array.isArray(preloadForm) ? preloadForm.length > 0 : !!preloadForm;
    const hasLinkedFormTemplateIds = Array.isArray(linkedFormTemplateIds)
      ? linkedFormTemplateIds.length > 0
      : !!linkedFormTemplateIds;
    const hasForm = hasPreloadForm || hasLinkedFormTemplateIds || linkedSubmissionIds?.length;

    if (timecardPreloadTables?.length && hasProject) {
      const existingTimecardTables = await getRelevantTimecardTables({
        timecardPreloadTables,
        divisions,
        preloadDateField,
        preloadDateModifier,
        preloadDateCount,
        preloadProject,
        linkedStart: linkedStart ? DateTime.fromMillis(linkedStart) : null,
        linkedEnd: linkedEnd ? DateTime.fromMillis(linkedEnd) : null,
        linkedProjectId,
      });

      const filteredTimecardTables = filterTableItemsByCostcode({
        tableItems: existingTimecardTables ?? [],
        costcodeName: linkedCostcodeName,
        costcodeCode: linkedCostcodeCode,
        costcodeMap,
      });

      relevantValues = filteredTimecardTables.map((v) => ({
        ...v,
        isPreloaded: true,
      }));
    }

    let preloadSourceFormIds = [];

    if (hasProject && hasForm) {
      const existingForms = await getRelevantForms({
        preloadDateField,
        preloadDateModifier,
        preloadDateCount,
        preloadProject,
        preloadForm,
        linkedStart: linkedStart ? DateTime.fromMillis(linkedStart) : null,
        linkedEnd: linkedEnd ? DateTime.fromMillis(linkedEnd) : null,
        linkedProjectId,
        linkedFormTemplateIds,
        formSubmissionIds: preloadSubmissionDropdown ? linkedSubmissionIds : null,
        filterByTimeTrackingGridDate: type === 'Equipment',
      });

      preloadSourceFormIds = existingForms?.map((form) => form.id) ?? [];

      existingForms?.forEach((form) => {
        const {
          data: { sections = [] } = {},
        } = form;

        const sourceLabel = `${form.templateName || 'Form'} ${form.number || ''}`.trim();

        if (sectionParser) {
          const {
            relevantValues: parsedValues,
            relevantColumns: parsedColumns,
          } = sectionParser({
            sourceLabel,
            sections,
            date: linkedStart ? DateTime.fromMillis(linkedStart).toISODate() : null,
            columns: relevantColumns,
            rows: relevantValues,
          });
          relevantValues = parsedValues;
          relevantColumns = parsedColumns;
          return;
        }

        sections.forEach(({ fields = [] }) => {
          fields.forEach(({ response = {}, type: fieldType }) => {
            const {
              values = [],
              columns: responseColumns = [],
              dataType: tableDataType,
              date: timeTrackingGridDate,
            } = response;
            if (tableDataType !== type && !(type === 'Equipment' && fieldType === 'timeTrackingGrid')) return;

            // HARBOUR-8603
            // Backend retrieves forms based on time tracking grid date and also submission
            // so we must filter here to ensure we only get values for the correct date
            if (fieldType === 'timeTrackingGrid') {
              if (!timeTrackingGridDate) return;
              const { startTime, endTime } = getStartEndTimes({
                preloadDateField,
                preloadDateModifier,
                preloadDateCount,
                linkedStart: linkedStart ? DateTime.fromMillis(linkedStart) : null,
                linkedEnd: linkedEnd ? DateTime.fromMillis(linkedEnd) : null,
              });

              const startMillis = startTime.toMillis();
              const endMillis = endTime.toMillis();
              const gridDateMs = DateTime.fromISO(timeTrackingGridDate).toMillis();

              if (gridDateMs < startMillis || gridDateMs > endMillis) return;
            }

            const filteredValues = fieldType === 'timeTrackingGrid'
              ? filterTimeTrackingGridValuesByCostcode({
                timeTrackingGridValues: values ?? [],
                columns: responseColumns ?? [],
                costcodeName: linkedCostcodeName,
                costcodeCode: linkedCostcodeCode,
                costcodeMap,
              })
              : filterTableItemsByCostcode({
                tableItems: values ?? [],
                costcodeName: linkedCostcodeName,
                costcodeCode: linkedCostcodeCode,
                costcodeMap,
              });

            relevantValues.push(
              ...filteredValues.map((value) => ({
                ...value,
                isPreloaded: true,
                sourceForm: sourceLabel,
              })),
            );
          });
        });
      });
    }

    setResponses((prev) => {
      const responseData = {
        values: relevantValues,
        columns: relevantColumns?.length ? relevantColumns : columns,
        dataType: type,
        date: linkedStart ? DateTime.fromMillis(linkedStart).toISODate() : null,
        timezone: linkedStart ? DateTime.fromMillis(linkedStart).zoneName : null,
        preloadSourceFormIds,
      };

      if (forceReload) {
        return {
          ...(prev ?? {}),
          [id]: responseData,
        };
      }

      const prevValues = prev?.[id]?.values ?? [];
      if (prevValues.length === 0 && relevantValues.length === 0) {
        return prev;
      }

      return {
        ...(prev ?? {}),
        [id]: responseData,
      };
    });
    setLastGetConfig({
      lastDateValue: linkedDateValue,
      lastProjectId: linkedProjectId,
      lastFormTemplateIds: normalizeToArray(linkedFormTemplateIds),
      lastSubmissionIds: linkedSubmissionIds,
      lastCostcodeName: linkedCostcodeName,
      lastCostcodeCode: linkedCostcodeCode,
    });
    if (typeof setLoading === 'function') setLoading(false);
  }, [
    responding,
    preloadExistingEntries,
    preloadProject,
    lastGetConfig,
    preloadDateField,
    linkedDateValue,
    preloadDropdown,
    linkedProjectId,
    preloadFormDropdown,
    preloadForm,
    linkedFormTemplateIds,
    preloadSubmissionDropdown,
    linkedSubmissionDropdown,
    timecardPreloadTables,
    divisions,
    id,
    setResponses,
    columns,
    type,
    setLoading,
    sectionParser,
    preloadCostcodeDropdown,
    linkedCostcodeName,
    linkedCostcodeCode,
    costcodeMap,
    skipPreload,
  ]);

  // pre-load entries
  useEffect(() => {
    if (skipPreload) return;
    debouncer.debounce(() => preloadAll(false), 500);
  }, [
    configProps,
    id,
    setResponses,
    linkedProjectId,
    linkedFormTemplateIds,
    linkedDateValue?.startTime,
    linkedDateValue?.endTime,
    type,
    linkedSubmissionDropdown,
    lastGetConfig,
    divisions,
    preloadAll,
    debouncer,
    linkedCostcodeName,
    linkedCostcodeCode,
    skipPreload,
  ]);

  return {
    triggerPreload: () => preloadAll(true),
  };
}
